import React, { FormEvent, useId, useState } from 'react';

/**
 * One field, one button, one line of feedback.
 *
 * Used where a page would otherwise be a dead end — a city we don't serve yet,
 * or a web booking flow that isn't live. The point is to keep the ask small
 * enough that answering it costs nothing; anything longer belongs on the
 * contact form.
 *
 * Inherits its colours from the surface it sits on, so the same component works
 * on paper and inside a `.sw-on-dark` section.
 */

type CaptureStatus = 'idle' | 'sending' | 'sent' | 'error';

interface InlineCaptureProps {
  /** Describes the field for assistive tech; visible only if `showLabel`. */
  label: string;
  placeholder: string;
  buttonLabel: string;
  type?: 'email' | 'tel';
  /** Resolve with the message to show on success; throw to show an error. */
  onSubmit: (value: string) => Promise<string>;
  showLabel?: boolean;
}

const InlineCapture: React.FC<InlineCaptureProps> = ({
  label,
  placeholder,
  buttonLabel,
  type = 'email',
  onSubmit,
  showLabel = false,
}) => {
  const fieldId = useId();
  const [value, setValue] = useState('');
  const [status, setStatus] = useState<CaptureStatus>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'sending') return;

    setStatus('sending');
    setMessage('');

    try {
      const success = await onSubmit(value.trim());
      setStatus('sent');
      setMessage(success);
      setValue('');
    } catch (error) {
      setStatus('error');
      setMessage(
        error instanceof Error && error.message.trim()
          ? error.message
          : 'That did not go through. Please try again.',
      );
    }
  };

  return (
    <form className="sw-capture" noValidate={false} onSubmit={handleSubmit}>
      <label
        className={showLabel ? 'sw-capture-label' : 'sw-visually-hidden'}
        htmlFor={fieldId}
      >
        {label}
      </label>

      <div className="sw-capture-row">
        <input
          className="sw-capture-input"
          id={fieldId}
          inputMode={type === 'tel' ? 'tel' : 'email'}
          name={type === 'tel' ? 'phone' : 'email'}
          onChange={(event) => setValue(event.target.value)}
          placeholder={placeholder}
          required
          type={type}
          value={value}
        />
        <button className="sw-btn-secondary" disabled={status === 'sending'} type="submit">
          {status === 'sending' ? 'Sending…' : buttonLabel}
        </button>
      </div>

      {/* Announced on change so the outcome reaches screen readers too. */}
      <p aria-live="polite" className="sw-capture-status" role="status">
        {message}
      </p>
    </form>
  );
};

export default InlineCapture;
